import React from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "../../../app/hook";
import { useGetAllScenariosQuery } from "../../../app/services/chatbot-rtk.services";
import Pie from "../../../components/ui/PieChart";
import { selectIsUserNew } from "../../../features/auth/auth.slice";
import { selectClient } from "../../../features/dashboard.slice";
import usePermission from "../../../hooks/usePermission";
import { BotTypeEnum } from "../../../utils/BotTypeEnum";

const DashboardHeaderSection: React.FC = () => {
  const { t } = useTranslation();

  const permission = usePermission();
  const isUserNew = useAppSelector(selectIsUserNew);

  const clientInfo = useAppSelector(selectClient);

  const { data: allScenarios } = useGetAllScenariosQuery(
    { botType: "widget" },
    {
      skip: isUserNew
    }
  );

  return (
    <div className="tw-px-4 tw-py-6 tw-bg-white tw-border-t tw-border-gray-200">
      <div className="tw-grid tw-grid-cols-12 tw-gap-5 tw-items-center">
        <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex xl:tw-col-span-5 lg:tw-col-span-5 tw-col-span-12">
          <div className="tw-text-gray-700 tw-text-base tw-font-medium tw-font-['Lexend'] tw-leading-normal lg:tw-mb-0 tw-mb-3">
            {t("DashboardHeaderSection/key1")}
          </div>
          <div className="tw-justify-start tw-items-center tw-gap-4 tw-inline-flex tw-flex-wrap">
            <div className="tw-justify-start tw-items-center tw-gap-1.5 tw-flex">
              <div className="tw-w-4 tw-h-4 tw-px-[5px] tw-bg-blue-100 tw-rounded-[99px] tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                <div className="tw-text-blue-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  1
                </div>
              </div>
              <div className="tw-text-gray-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("DashboardHeaderSection/key2")}
              </div>
            </div>
            <div className="tw-justify-start tw-items-center tw-gap-1.5 tw-flex">
              <div className="tw-w-4 tw-h-4 tw-px-[5px] tw-bg-blue-100 tw-rounded-[99px] tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                <div className="tw-text-blue-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  2
                </div>
              </div>
              <div className="tw-text-gray-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("DashboardHeaderSection/key3")}
              </div>
            </div>
            <div className="tw-justify-start tw-items-center tw-gap-1.5 tw-flex">
              <div className="tw-w-4 tw-h-4 tw-px-[5px] tw-bg-blue-100 tw-rounded-[99px] tw-flex-col tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                <div className="tw-text-blue-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  3
                </div>
              </div>
              <div className="tw-text-gray-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t("DashboardHeaderSection/key4")}
              </div>
            </div>
          </div>
        </div>

        <div className="xl:tw-col-span-7 lg:tw-col-span-7 tw-col-span-12">
          <div className="tw-grid tw-grid-cols-12 lg:tw-gap-0 tw-gap-6">
            <div className="lg:tw-col-span-6 tw-col-span-12 tw-flex tw-items-start lg:tw-justify-end tw-justify-between">
              <div className="tw-h-10 tw-w-[2px] tw-bg-gray-100 tw-mr-4 lg:tw-inline-flex tw-hidden" />
              <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex lg:tw-w-[187px]">
                <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                  <div className="tw-text-gray-700 tw-text-base tw-font-medium tw-font-['Lexend'] tw-leading-normal">
                    {t("Dashboard/scenario-bots")}
                  </div>
                  {/* TODO => Identify the logic required for badge and uncomment following */}
                  {/* <div className="tw-px-1.5 tw-pt-px tw-pb-[3px] tw-bg-gray-100 tw-rounded-md tw-justify-start tw-items-center tw-flex">
                    <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                      Free
                    </div>
                  </div> */}
                </div>
                <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                  <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {allScenarios?.find(
                      (item) =>
                        item.type === BotTypeEnum.TEXT_SCENARIO ||
                        item.type === BotTypeEnum.VIDEO_SCENARIO
                    )
                      ? t("DashboardHeaderSection/key8")
                      : t("DashboardHeaderSection/key7")}
                  </div>
                </div>
              </div>
              <div className="tw-justify-start tw-items-center tw-gap-2 tw-flex">
                <div className="tw-flex-col tw-justify-center tw-items-end tw-inline-flex">
                  <div className="tw-text-gray-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {t("DashboardHeaderSection/key9")}
                  </div>
                  <div className="tw-text-gray-700 tw-text-base tw-font-medium tw-font-['Lexend'] tw-leading-normal">
                    {clientInfo?.remainingSessions?.scenarioUsed}
                    {" / "}
                    {clientInfo?.remainingSessions?.scenarioBasedLimit &&
                    clientInfo?.remainingSessions?.scenarioBasedLimit < 0 ? (
                      <span className="tw-text-xl">&#8734;</span>
                    ) : (
                      <span>
                        {clientInfo?.remainingSessions?.scenarioBasedLimit ?? 0}
                      </span>
                    )}
                  </div>
                </div>
                <div className="tw-w-10 tw-h-10 tw-relative">
                  <Pie
                    percentageFull={
                      (100 *
                        (clientInfo?.remainingSessions?.scenarioUsed ?? 0)) /
                      (clientInfo?.remainingSessions?.scenarioBasedLimit ?? 0)
                    }
                  />
                </div>
              </div>
            </div>

            <div className="lg:tw-hidden tw-col-span-12 tw-w-full tw-h-[2px] tw-bg-gray-100" />

            <div className="lg:tw-col-span-6 tw-col-span-12 tw-flex tw-items-start lg:tw-justify-end tw-justify-between">
              <div className="tw-h-10 tw-w-[2px] tw-bg-gray-100 tw-mr-4 lg:tw-inline-flex tw-hidden" />
              <div className="tw-flex-col tw-justify-center tw-items-start tw-gap-1 tw-inline-flex lg:tw-w-[187px]">
                <div className="tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                  <div className="tw-text-gray-700 tw-text-base tw-font-medium tw-font-['Lexend'] tw-leading-normal">
                    {t("Dashboard/ai-bots") || "AI sessions"}
                  </div>
                </div>
                <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {allScenarios?.find(
                    (item) =>
                      item.type !== BotTypeEnum.TEXT_SCENARIO &&
                      item.type !== BotTypeEnum.VIDEO_SCENARIO
                  )
                    ? t("DashboardHeaderSection/key8")
                    : t("DashboardHeaderSection/key7")}
                </div>
              </div>
              <div className="tw-justify-start tw-items-center tw-gap-2 tw-flex">
                <div className="tw-flex-col tw-justify-center tw-items-end tw-inline-flex">
                  <div className="tw-text-gray-700 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {t("DashboardHeaderSection/key9")}
                  </div>
                  <div className="tw-text-gray-700 tw-text-base tw-font-medium tw-font-['Lexend'] tw-leading-normal">
                    {clientInfo?.remainingSessions?.aiUsed}
                    {" / "}
                    {clientInfo?.remainingSessions?.aiBasedLimit &&
                    clientInfo?.remainingSessions?.aiBasedLimit < 0 ? (
                      <span className="tw-text-xl">&#8734;</span>
                    ) : (
                      <span>
                        {clientInfo?.remainingSessions?.aiBasedLimit ?? 0}
                      </span>
                    )}
                  </div>
                </div>
                <div className="tw-w-10 tw-h-10 tw-relative">
                  <Pie
                    percentageFull={
                      (100 * (clientInfo?.remainingSessions?.aiUsed ?? 0)) /
                      (clientInfo?.remainingSessions?.aiBasedLimit ?? 0)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeaderSection;
